/**
 * usePlanDetails
 *
 * Fetch ONE plan by id, for the `?plan=` deep link.
 *
 * Only fires when the plan is NOT in the available-subscriptions list — which is
 * the definition of an unlisted plan. A promo link, the common case, points at a
 * listed plan and therefore costs zero extra requests.
 *
 * **This endpoint's answer depends on WHO is asking.** A plan carrying a
 * new-member anchor is 404 for a guest and 200 for a grandfathered member, so
 * two things here are load-bearing:
 *
 * 1. **Nothing fetches until auth has settled.** `AuthProvider` renders its
 *    children immediately and restores the session asynchronously, so without
 *    this gate a signed-in member opening a link cold would fetch before the
 *    token was attached, get the guest answer, and cache it.
 * 2. **The viewer id is in the query key.** A session change re-keys the query
 *    rather than relying on an invalidation list staying in sync with it — the
 *    auth invalidator matches whole segments (`'subscriptions'`, not
 *    `'passes'`) and would not have caught this one.
 *
 * `useAuthenticatedQuery` is deliberately NOT used: it disables itself when
 * unauthenticated, and this path must serve guests.
 *
 * @module subscription/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useClubId } from '~/services/appRuntime';
import { useAuth } from '~/services/auth';
import { passService } from '../api/passService';
import { membershipService } from '../api/membershipService';
import { formatMembershipPlan, formatPassPlan } from '../utils/formatPlan';
import type { FormattedSubscriptionPlan } from '../types';
import type { ParsedSubscriptionId } from '~/lib/utils/subscriptionIdUtils';

export interface UsePlanDetailsOptions {
  /** The parsed `?plan=` param, or null when there isn't one. */
  parsed: ParsedSubscriptionId | null;
  /**
   * Gate. The caller passes `false` until the list has settled AND missed —
   * a listed plan must never trigger a second request.
   */
  enabled: boolean;
}

/**
 * Returns the formatted plan, or null. `isPending` is only meaningful while
 * `enabled`.
 */
export function usePlanDetails({ parsed, enabled }: UsePlanDetailsOptions) {
  const [clubId] = useClubId();
  const { user, isLoading: authLoading } = useAuth();
  const clubIdNumber = clubId ? Number(clubId) : null;

  // `wanted` is the caller's intent; `active` is whether we may act on it yet.
  // They differ only while auth is settling, and keeping them apart is what
  // stops `settled` below reporting "no plan" during that window.
  const wanted = enabled && !!parsed && !!clubIdNumber;
  const active = wanted && !authLoading;
  const viewerId = user?.id ?? null;

  const query = useQuery({
    queryKey: active
      ? parsed!.type === 'pass'
        ? queryKeys.passes.detail(clubIdNumber!, String(parsed!.id), viewerId)
        : queryKeys.memberships.detail(clubIdNumber!, String(parsed!.id), viewerId)
      : queryKeys.disabled.passes(),
    queryFn: async (): Promise<FormattedSubscriptionPlan | null> => {
      if (!parsed) return null;

      if (parsed.type === 'pass') {
        const result = await passService.getPassDetails(parsed.id);
        // A 404 here is the normal answer for a private, limited, or simply
        // absent plan. Null rather than a throw: the caller falls back to the
        // selection step silently, which is the product decision.
        if (!result.success || !result.payload?.pass) return null;
        // `formatPassPlan` still takes the Drizzle row type. Its other caller is
        // the LISTING, whose response type is deliberately left Date-keyed —
        // see the KNOWN DEBT on `ApiAvailableSubscriptionsResponse`, which defers
        // the honest typing because it cascades into the shared Membership/Pass
        // cards used by all three apps. The formatter reads no date and no
        // policy field, so the cast is safe; it goes when that pass happens.
        return formatPassPlan(result.payload.pass as unknown as Parameters<typeof formatPassPlan>[0]);
      }

      const result = await membershipService.getMembershipDetails(parsed.id);
      if (!result.success || !result.payload?.membership) return null;
      return formatMembershipPlan(
        result.payload.membership as unknown as Parameters<typeof formatMembershipPlan>[0],
      );
    },
    enabled: active,
    // Same ageing as the listing, so a plan reached by link and a plan reached
    // by the grid never disagree about how stale they are.
    ...cachePresets.stable,
  });

  return {
    plan: query.data ?? null,
    // Pending covers the auth wait too — the caller holds its skeleton rather
    // than flashing the selection step at someone who followed a link.
    isPending: wanted && (authLoading || query.isPending),
    /**
     * True once the fetch has produced an answer, success or not.
     *
     * Keyed off `wanted`, NOT `active`: a caller who asked for a plan while auth
     * was still initializing must keep waiting, not read this as "settled, no
     * plan" and silently drop the link.
     */
    settled: !wanted || (active && (query.isSuccess || query.isError)),
  };
}
